import { Reveal } from "@/components/Reveal";

/**
 * Die beiden Kernprobleme, je einem Blickwinkel zugeordnet. Bewusst als
 * typografische Blöcke mit Trennlinie gesetzt - der Abschnitt „Der Weg zur
 * Partnerschaft" weiter unten nutzt dieselbe Sticky-Spalte, dort aber
 * nummerierte Karten.
 */
const SIDES = [
  {
    tone: "orange",
    label: "Auf der einen Seite",
    who: "Sportvereine",
    claim: "Die Sponsorensuche kostet am meisten Kraft und liefert am wenigsten zurück.",
    text: "Sie läuft nach Feierabend, neben Training, Spielbetrieb und Ehrenamt: recherchieren, Mappen bauen, nachfassen, hoffen. Für zwei, drei Zusagen gehen ganze Abende drauf. Und im nächsten Sommer beginnt dasselbe von vorn.",
    facts: ["Recherche ohne Übersicht", "Unterlagen in Handarbeit", "Jedes Jahr von vorn"],
  },
  {
    tone: "blue",
    label: "Auf der anderen Seite",
    who: "Unternehmen",
    claim: "Was das Sponsoring gebracht hat, weiß am Ende niemand.",
    text: "Schon die Auswahl ist ein Ratespiel: Welcher Verein zur Marke passt, steht nirgends vergleichbar nebeneinander. Und läuft die Partnerschaft erst, gibt es keine einzige Zahl dazu. Wer im nächsten Budget dafür streiten muss, hat nichts in der Hand.",
    facts: ["Angebote nicht vergleichbar", "Keine Kennzahlen", "Nichts zum Vorlegen"],
  },
];

export const Problem = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute top-1/4 -left-32 w-[560px] h-[440px] rounded-full bg-brand-orange/10 blur-[170px]" />
      </div>

      <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 xl:gap-20">
        {/* Sticky links */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-sticky">
            <Reveal>
              <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-brand-orange mb-6">
                Die Ausgangslage
              </p>
              <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
                Der Wille ist da.
                <br />
                Die <span className="gradient-text">Struktur</span> fehlt.
              </h2>
              <p className="mt-6 max-w-sm text-base md:text-lg leading-relaxed text-white/55">
                Sponsoring im Breitensport scheitert selten am Geld. Es scheitert
                daran, wie viel Arbeit es macht und wie wenig davon am Ende
                belegbar ist.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Zwei typografische Blöcke */}
        <div className="lg:col-span-8">
          {SIDES.map((side, i) => (
            <Reveal key={side.who} delay={i * 0.08}>
              <div
                className={`group ${
                  i > 0 ? "mt-20 pt-20 border-t border-white/10" : ""
                }`}
              >
                <div className="flex items-baseline gap-3 mb-6">
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      side.tone === "orange"
                        ? "bg-brand-orange shadow-[0_0_10px_2px_rgba(255,107,0,0.6)]"
                        : "bg-brand-blue shadow-[0_0_10px_2px_rgba(0,122,255,0.6)]"
                    }`}
                  />
                  {/* Ein fortlaufender Text, damit er auf dem Handy zwischen
                      den Wörtern umbricht statt in zwei Spalten */}
                  <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/35">
                    {side.label}{" "}
                    <span className="whitespace-nowrap text-white/60">· {side.who}</span>
                  </span>
                </div>

                <p className="font-display font-bold text-2xl md:text-3xl lg:text-4xl leading-[1.12] text-white max-w-2xl">
                  {side.claim}
                </p>

                <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-white/55">
                  {side.text}
                </p>

                <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                  {side.facts.map((f) => (
                    <li
                      key={f}
                      className="text-xs uppercase tracking-[0.15em] font-bold text-white/30"
                    >
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.15}>
            <p className="mt-16 max-w-2xl text-base md:text-lg leading-relaxed text-white/70">
              Zwischen beiden liegt kein Geldproblem, sondern ein
              Strukturproblem.{" "}
              <span className="text-white">
                Genau an diesen zwei Punkten setzt MatchGate an.
              </span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
